<template>
  <header ref="headerEl">
    <div class="header-inner">
      <NuxtLink to="/" class="logo-area">
        <h1 class="logo">
          <img src="/image/common/header/logo.png" alt="オリエンタルバイオ" />
        </h1>
        <div class="tagline">明日への希望を、暮らしのそばに。</div>
      </NuxtLink>

      <div class="header-actions">
        <div class="header-btn-wrap">
          <NuxtLink v-if="!isLoggedIn" to="/login" class="header-btn btn-login">
            <div class="icon_login">
              <img src="/image/common/header/icon_login.png" alt="" />
            </div>
            ログイン
          </NuxtLink>
          <a v-else href="#" class="header-btn btn-login" @click.prevent="logout">
            <div class="icon_login">
              <img src="/image/common/header/icon_login.png" alt="" />
            </div>
            ログアウト
          </a>

          <NuxtLink :to="isLoggedIn ? '/mypage' : '/register'" class="header-btn btn-regist">
            <div class="icon_member-regist">
              <img class="pc-only" src="/image/common/header/icon_member-regist_white.png" alt="" />
              <img class="sp-only" src="/image/common/header/icon_member-regist_green.png" alt="" />
            </div>
            {{ isLoggedIn ? 'マイページ' : '会員登録' }}
          </NuxtLink>
        </div>

        <NuxtLink to="/cart" class="cart">
          <div class="icon_cart">
            <img src="/image/common/header/icon_cart.png" alt="カート" />
          </div>
          <div class="cart-badge">{{ cartCount }}</div>
        </NuxtLink>

        <button
          ref="hamButtonEl"
          type="button"
          class="ham-btn"
          :class="{ 'is-open': isMenuOpen }"
          :aria-expanded="isMenuOpen"
          aria-controls="global-nav"
          :aria-label="isMenuOpen ? 'メニューを閉じる' : 'メニューを開く'"
          @click="toggleMenu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
    </div>
  </header>

  <!-- ヘッダーナビゲーション -->
  <nav>
    <div
      id="global-nav"
      ref="navInnerEl"
      class="header-nav-inner"
      :class="{ 'is-open': isMenuOpen }"
      :style="isMenuOpen ? { paddingTop: `${headerHeight}px` } : undefined"
    >
      <ul class="header-nav-links">
        <template v-for="(item, index) in GLOBAL_NAV_ITEMS" :key="item.label">
          <span v-if="index > 0"></span>
          <li>
            <PartsChromeLink :to="item.to" :external="item.external">
              <div class="header-nav-icon" :class="item.iconModifier">
                <img :src="item.icon" alt="" />
              </div>
              <div class="header-nav-text">
                {{ item.labelLines[0] }}<br />{{ item.labelLines[1] }}
              </div>
            </PartsChromeLink>
          </li>
        </template>
      </ul>

      <!--
        `div` ＋ `@keyup.enter` だと日本語IMEの変換確定 Enter で誤送信する（keyup は composition
        終了後に `Enter` として発火するため）。`form` の submit は変換確定の Enter では発火しない。
        併せて `enterkeyhint` でモバイルのソフトキーボードに「検索」キーを出す。
        支給CSS のセレクタは `.search-bar` / `.search-bar input` でタグ名を限定していないため
        `div` → `form` に変えても見た目は変わらない（`form` は `renewal/reset.css:3-15` の
        一括リセットに入っており UA 既定が残らない）。

        **虫眼鏡は `div` → `button` にしている。こちらはリセットに入っていない**ので
        UA 既定の `padding` が残る。`<style scoped>` で打ち消している（#1324）。
      -->
      <form class="search-bar" role="search" @submit.prevent="submitSearch">
        <button type="submit" class="icon_search" aria-label="検索する">
          <img src="/image/icon_search.png" alt="" />
        </button>
        <input
          v-model="keyword"
          type="search"
          name="q"
          :maxlength="SEARCH_MAX_KEYWORD_LENGTH"
          enterkeyhint="search"
          placeholder="商品名・キーワード"
          aria-label="商品名・キーワードで検索"
        />
      </form>

      <div class="ham-menu-content">
        <div class="footer-nav-accordian">
          <!-- カテゴリはトップの「人気の商品ランキング」へ、該当タブを選んだ状態で飛ばす（決定#26） -->
          <div v-for="category in categories" :key="category.id" class="footer-nav-accordian_item">
            <NuxtLink
              :to="{
                path: '/',
                query: { [PRODUCTS_CATEGORY_QUERY]: category.id },
                hash: PRODUCTS_ANCHOR_HASH,
              }"
              class="footer-nav-accordian_title"
            >
              {{ category.title }}
              <span class="footer-nav-accordian_icon" aria-hidden="true"></span>
            </NuxtLink>
          </div>

          <div
            v-for="section in CHROME_LINK_SECTIONS"
            :key="section.id"
            class="footer-nav-accordian_item"
            :class="{ 'is-open': openSections.has(section.id) }"
          >
            <button
              type="button"
              class="footer-nav-accordian_title"
              :aria-expanded="openSections.has(section.id)"
              @click="toggleSection(section.id)"
            >
              {{ section.title }}
              <span class="footer-nav-accordian_icon" aria-hidden="true"></span>
            </button>
            <PartsCollapse :show="openSections.has(section.id)">
              <div class="footer-nav-accordian_body">
                <ul class="footer-nav_list">
                  <li v-for="link in section.links" :key="link.label">
                    <PartsChromeLink :to="link.to" :external="link.external">
                      {{ link.label }}
                    </PartsChromeLink>
                  </li>
                </ul>
              </div>
            </PartsCollapse>
          </div>
        </div>

        <OrganismsLayoutsContactBox />

        <div class="ham-menu-footer">
          <div class="footer-logo">
            <NuxtLink to="/">
              <img src="/image/common/header/logo.png" alt="オリエンタルバイオ" />
            </NuxtLink>
          </div>
          <p class="copyright">© {{ currentYear }} ORIENTALBIO Co.,Ltd.</p>
        </div>
      </div>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { useAuth } from '@/composables/useAuth';
import { useCart } from '@/composables/useCart';
import {
  GLOBAL_NAV_ITEMS,
  CHROME_LINK_SECTIONS,
  PRODUCTS_ANCHOR_HASH,
  PRODUCTS_CATEGORY_QUERY,
} from '@/consts/ChromeConst';
import { CATEGORY_DISPLAY_ORDER, PRODUCT_CONTENT_CONSTANTS } from '@/consts/ProductContentConst';
import { SEARCH_MAX_KEYWORD_LENGTH } from '@/consts/SearchConst';
import { LayoutPresenter } from '@/presenters/LayoutPresenter';

const presenter = new LayoutPresenter();
const router = useRouter();
const route = useRoute();
const { currentUser } = useAuth();
const { cartRelatedInformation } = useCart();

const isLoggedIn = computed<boolean>(() => currentUser.value !== null);
const cartCount = computed<number>(() => cartRelatedInformation.value.carts.length);
const currentYear: number = new Date().getFullYear();

/**
 * ハンバーガー内のカテゴリ見出し。商品リストは出さずトップの `#products` へ飛ばすだけなので、
 * カテゴリ名だけを `ProductContentConst` から引く。
 * 並びは支給デザインの表示順（サプリメント → 食品 → スキンケア → パーソナルケア）で、ID の昇順ではない。
 *
 * `id` はカテゴリ英名で、そのまま `?category=` の値になる（`products.vue` がタブに解決する）。
 */
const categories = computed(() =>
  CATEGORY_DISPLAY_ORDER.map((id) => ({
    id: PRODUCT_CONTENT_CONSTANTS.CATEGORY_ENGLISH_NAME[id],
    title: PRODUCT_CONTENT_CONSTANTS.CATEGORY_JAPANESE_NAME[id],
  })).filter(({ id }) => id !== undefined)
);

/**
 * キーワード検索。`/search?q=` は決定済み（未決#13）。
 *
 * **入力欄は現在の `q` を映す。** 空で始めると、`/search?q=A` を開き直したり
 * ブラウザバックで戻ったりしたときに、URL・見出し・件数は `A` なのに**入力欄だけ別の語**
 * （または空）になる。`/search` は入力欄と表示内容の一致が意味を持つ画面なので合わせる
 * （PR #1294 のレビュー指摘⑧）。
 *
 * 入力中に URL は変わらないので、打っている途中で書き戻されることはない。
 */
const keyword = ref<string>('');

const currentQuery = computed<string>(() => {
  const q = route.query.q;
  return (Array.isArray(q) ? (q[0] ?? '') : (q ?? '')).toString();
});

watch(currentQuery, (q: string) => (keyword.value = q), { immediate: true });
const submitSearch = (): void => {
  const q = keyword.value.trim();
  if (!q) return;
  closeMenu();
  router.push({ path: '/search', query: { q } });
};

/** フッター／ハンバーガーのアコーディオン */
const openSections = ref<Set<string>>(new Set());
const toggleSection = (id: string): void => {
  const next = new Set(openSections.value);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  openSections.value = next;
};

/** ハンバーガーメニュー */
const SP_BREAKPOINT = 768;
const headerEl = ref<HTMLElement | null>(null);
const navInnerEl = ref<HTMLElement | null>(null);
const hamButtonEl = ref<HTMLButtonElement | null>(null);
const isMenuOpen = ref<boolean>(false);
const headerHeight = ref<number>(0);

const syncBodyLock = (locked: boolean): void => {
  if (!import.meta.client) return;
  document.body.classList.toggle('is-menu-open', locked);
  // 開いている間は背後を `inert` で無効化し、支援技術からも隠す（全画面オーバーレイのため）。
  // `main, footer` だけでは足りない —— `layouts/default.vue` は `</main>` と `<footer>` の間に
  // 共通フッタ3ブロックと SP サイドバーを直接置いており、それらを取りこぼすので
  // 「**器の直下**から `<header>` / `<nav>` を除いた全部」を対象にする。
  // 器を掴む手掛かりは `#top`。**旧CSS の `.l-wrap` を使わない**（#1335 で外した）——
  // ヘッダーを載せるのは `layouts/default.vue` と `error.vue` の2つだけで、どちらも `id="top"` を持つ。
  // `inert` プロパティは TS の DOM 型定義のバージョン差に左右されるため属性で切り替える。
  const wrap = document.getElementById('top');
  wrap?.querySelectorAll<HTMLElement>(':scope > *').forEach((el) => {
    if (el.tagName === 'HEADER' || el.tagName === 'NAV') return;
    el.toggleAttribute('inert', locked);
  });
};

/**
 * メニュー内で「実際に見えている」フォーカス可能要素だけを返す。
 * 支給CSS はメニューを開いた瞬間に `.header-nav-links`（DOM 順で先頭）を `display: none` にし、
 * 代わりに `.ham-menu-content` を出す設計になっている。`display: none` の要素は `.focus()` を
 * 呼んでも何も起きずフォーカスがハンバーガーボタンに残ってしまうため、
 * `getClientRects()` でレイアウトを持つ要素だけに絞る。
 */
const getMenuFocusables = (): HTMLElement[] => {
  if (!navInnerEl.value) return [];
  return Array.from(
    navInnerEl.value.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
  ).filter((el) => el.getClientRects().length > 0);
};

/**
 * フォーカストラップの巡回対象。ハンバーガーボタンは `<header>` 内にあり `#global-nav` の外だが、
 * 開いている間は閉じる（×）ボタンとして画面上はメニューの一部なので、Tab で到達できるよう含める。
 */
const getTrapFocusables = (): HTMLElement[] => {
  const items = getMenuFocusables();
  return hamButtonEl.value ? [hamButtonEl.value, ...items] : items;
};

/**
 * メニューを開いている間のキー操作。Esc は `window` で拾う ——
 * ハンバーガーボタンは `<header>` 内にあり `#global-nav` の子孫ではなく兄弟なので、
 * `#global-nav` に `@keydown.esc` を張ってもボタンにフォーカスがある間はイベントが届かない。
 */
const handleKeydown = (e: KeyboardEvent): void => {
  if (e.key === 'Escape') {
    e.preventDefault();
    closeMenu();
    return;
  }
  if (e.key !== 'Tab') return;

  // フォーカストラップ。背後は `inert` で無効化済みだが、Tab の巡回自体もメニュー内で閉じる
  const items = getTrapFocusables();
  const first = items[0];
  const last = items.at(-1);
  if (!first || !last) return;
  if (e.shiftKey && document.activeElement === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault();
    first.focus();
  }
};

const openMenu = async (): Promise<void> => {
  isMenuOpen.value = true;
  headerHeight.value = headerEl.value?.offsetHeight ?? 0;
  syncBodyLock(true);
  await nextTick();
  navInnerEl.value?.scrollTo({ top: 0 });
  // 開いたらメニュー内へフォーカスを移す（§3.6-2）。可視の要素に限る理由は
  // `getMenuFocusables` のコメントを参照
  getMenuFocusables()[0]?.focus();
};

const closeMenu = (): void => {
  if (!isMenuOpen.value) return;
  isMenuOpen.value = false;
  syncBodyLock(false);
  // 閉じたらトリガーへフォーカスを戻す（§3.6-2）
  hamButtonEl.value?.focus();
};

const toggleMenu = (): void => {
  if (isMenuOpen.value) {
    closeMenu();
  } else {
    openMenu();
  }
};

const handleResize = (): void => {
  if (!isMenuOpen.value) return;
  if (window.innerWidth > SP_BREAKPOINT) {
    isMenuOpen.value = false;
    syncBodyLock(false);
  } else {
    headerHeight.value = headerEl.value?.offsetHeight ?? 0;
  }
};

// キーリスナーは開いている間だけ張る（閉じている間の Esc / Tab は一切拾わない）
watch(isMenuOpen, (open: boolean) => {
  if (!import.meta.client) return;
  if (open) {
    window.addEventListener('keydown', handleKeydown);
  } else {
    window.removeEventListener('keydown', handleKeydown);
  }
});

onMounted(() => window.addEventListener('resize', handleResize));
onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
  window.removeEventListener('keydown', handleKeydown);
  syncBodyLock(false);
});

router.afterEach(() => {
  isMenuOpen.value = false;
  syncBodyLock(false);
});

const logout = async (): Promise<void> => {
  await presenter.logout();
};
</script>

<style scoped>
/**
 * **虫眼鏡アイコンの UA 既定 padding を打ち消す。**
 *
 * 支給は `<div class="icon_search">`（`index.html:152-154`）だが、実装は検索を実行する
 * **本物の送信ボタン**にするため `<button type="submit">` にしている。
 * `button` はどのリセットにも入っておらず（`renewal/reset.css:3-15` の列挙に無く、
 * `renewal/style.css:65-69` の `button {}` は `border` / `background-color` / `cursor` だけ）、
 * **UA 既定の `padding: 1px 6px` が残る**。`* { box-sizing: border-box }`（`同:9-10`）なので
 * 指定幅の内側から左右 6px ずつ食われ、**PC は 20px → 中身 6.9px、SP は 3.61vw → 中身 1.4px**
 * まで潰れて画像が消えて見えていた。
 *
 * 支給の実測は **PC 16.98px / SP 13.53px・`padding: 0`**（`.search-bar` の flex で
 * 20px から縮んだ値）。**縮む挙動ごと支給と同じ**なので `flex-shrink` は足さない。
 */
.icon_search {
  padding: 0;
}
</style>
